import { InformationContainer } from '@features/common';
import { Cities } from '@features/customers/interfaces/search.interfaces';
import {
	generateActualConsecutiveRules,
	generateFinalConsecutiveRules,
	INITIAL_CONSECUTIVE_RULES,
} from '@features/processes/constants/process-forms.constants';
import {
	ProcessEditionFormType,
	ProcessGuidesRangeFormType,
} from '@features/processes/interfaces/process-edition.interface';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
	Autocomplete,
	Button,
	Chip,
	FormControlLabel,
	IconButton,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
	handleProcessNumberKeyPress,
	handleProcessNumberPaste,
} from '../../processes/components/ProcessEditionForm/ProcessEditionForm.handlers';
import useRequests from '@hooks/requests.hook';
import ViewMessageSyncUp from '@features/common/ViewMessageSyncUp';
import ViewMessageDelete from '@features/common/ViewMessageDelete';
import SightLoading from '@features/common/Loading';
import { useSnackbar } from 'notistack';

interface Props {
	data: ProcessGuidesRangeFormType;
	cities: Cities[];
	form: UseFormReturn<ProcessEditionFormType>;
	index: number;
}

const ProcessRangeHistoryCard: React.FC<Props> = ({
	data,
	cities,
	form: { control, setValue, watch, setError },
	index,
}) => {
	// States
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isClientOpen, setIsClientOpen] = useState<boolean>(false);
	const [isTest, setIsTest] = useState<boolean>(false);
	const [isClose, setIsClose] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [errorResponse, setErrorResponse] = useState<boolean>(false);
	const [dataResponse, setDataResponse] = useState<any>({});
	const { requestAdministraCliente } = useRequests();
	const { enqueueSnackbar } = useSnackbar();
	// Variables
	const clienteGeneraNumeracion = watch('cliente_genera_numeracion');
	const consecutivoActual = Number(watch(`ranges.${index}.consecutivo_actual`)) || 0;
	const consecutivoDesde = Number(watch(`ranges.${index}.consecutivo_desde`)) || 0;
	const consecutivoHasta = Number(watch(`ranges.${index}.consecutivo_hasta`)) || 1;

	const formFields = {
		consecutivo_actual: consecutivoActual,
		consecutivo_desde: consecutivoDesde,
		consecutivo_hasta: consecutivoHasta,
	};

	// Effects
	useEffect(() => {
		const edited_fields: string[] = [];
		if (data.consecutivo_actual !== formFields.consecutivo_actual) {
			edited_fields.push('campo Actual');
		}

		if (data.consecutivo_desde !== formFields.consecutivo_desde) {
			edited_fields.push('campo Inicial');
		}

		if (data.consecutivo_hasta !== formFields.consecutivo_hasta) {
			edited_fields.push('campo Final');
		}

		setValue(`ranges.${index}.edited_fields`, edited_fields);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formFields]);

	useEffect(() => {
		const wheel = document?.getElementsByClassName('numberInput');
		for (let index = 0; index < wheel.length; index++) {
			wheel[index].addEventListener(
				'wheel',
				function (e) {
					e.preventDefault();
				},
				{ passive: false }
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const calculateValue = (ciudad: string | undefined): Cities | undefined => {
		return cities.find((city) => city.codigo.toString() === ciudad);
	};

	const closeCardtest = () => {
		setIsTest(false);
	};
	const closeCardClose = () => {
		setIsClose(false);
	};

	const sincronizarRangoTest = async (id_rango: number) => {
		setLoading(true);
		try {
			const response = (await requestAdministraCliente(
				'rangos/gateway/migracion-test',
				'POST',
				{
					id_rango: id_rango,
				}
			)) as any;

			// se valida la respuesta negativa de la petición
			if (response.isError === true) {
				setDataResponse(response.data);
				setErrorResponse(true);
				setLoading(false);
				setIsTest(true);
				// se envian datos de respuesta a ventana emergente de error y se muestran
			}

			if (response.isError === false) {
				setErrorResponse(false);
				setLoading(false);
				setIsTest(true);
			}
		} catch (error: any) {
			setLoading(false);
			if (error.response && error.response.data.message) {
				enqueueSnackbar(error.response.data.message, { variant: 'error' });
			} else if (typeof error?.message === 'string') {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		}
	};

	return (
		<>
			{isTest && (
				<ViewMessageSyncUp
					onClose={closeCardtest}
					errorResponse={errorResponse}
					dataResponse={dataResponse}
				/>
			)}
			{isClose && (
				<ViewMessageDelete
					onClose={closeCardClose}
					id_rango={data.id_rango}
					setIsOpen={setIsOpen}
				/>
			)}
			{loading && <SightLoading show={true} />}

			<InformationContainer
				// disabled={!data.en_uso && !isOpen}
				id={`process-edition-form-${data.id_rango}`}
				style={{
					borderRadius: 10,
					boxShadow: '0px 2px 7px #00000029',
					marginTop: 8,
					padding: 12,
					position: 'relative',
				}}
			>
				{!data.terminado && (
					<IconButton
						onClick={() => setIsOpen(!isOpen)}
						id="botonRange"
						size="small"
						sx={{ zIndex: 1, position: 'absolute', right: 12, top: 2 }}
					>
						{isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
					</IconButton>
				)}
				<Chip
					sx={{
						position: 'absolute',
						right: 47,
						top: 6,
						backgroundColor: data.terminado
							? '#686868'
							: data.en_uso
							? '#1ecb85'
							: '#f05a28',
						color: '#FFFFFF',
						fontSize: 12,
						height: 22,
					}}
					label={data.terminado ? 'Terminado' : data.en_uso ? 'Activo' : 'Inactivo'}
				/>
				{isOpen ? (
					<>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
						>
							Prefijo
						</Typography>
						<TextField
							disabled
							fullWidth
							inputProps={{ className: 'numberInput' }}
							className="numberInput"
							size="small"
							sx={{ marginTop: '8px' }}
							type="number"
							value={data.prefijo_consecutivo}
						/>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
							style={{ marginTop: 16 }}
						>
							Inicial
						</Typography>
						<Controller
							control={control}
							defaultValue={data.consecutivo_desde}
							name={`ranges.${index}.consecutivo_desde`}
							rules={INITIAL_CONSECUTIVE_RULES}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									className="numberInput"
									error={Boolean(error?.message)}
									helperText={error?.message}
									inputProps={{ min: 0, max: 9999999, className: 'numberInput' }}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="9.999.999"
									size="small"
									sx={{ marginTop: '8px' }}
									type="number"
									value={value}
								/>
							)}
						/>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
							style={{ marginTop: 16 }}
						>
							Final
						</Typography>
						<Controller
							control={control}
							defaultValue={data.consecutivo_hasta}
							name={`ranges.${index}.consecutivo_hasta`}
							rules={generateFinalConsecutiveRules(consecutivoDesde)}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									className="numberInput"
									error={Boolean(error?.message)}
									helperText={error?.message}
									inputProps={{
										min: consecutivoDesde,
										max: 9999999,
										className: 'numberInput',
									}}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="9.999.999"
									size="small"
									sx={{ marginTop: '8px' }}
									type="number"
									value={value}
								/>
							)}
						/>
						<Typography
							color="#707070"
							fontFamily="Montserrat"
							fontSize={14}
							fontWeight={500}
							style={{ marginTop: 8 }}
						>
							Actual
						</Typography>
						<Controller
							control={control}
							defaultValue={data.consecutivo_actual ?? data.consecutivo_desde}
							name={`ranges.${index}.consecutivo_actual`}
							rules={generateActualConsecutiveRules(
								clienteGeneraNumeracion,
								consecutivoDesde,
								consecutivoHasta
							)}
							render={({ field: { onChange, value }, fieldState: { error } }) => (
								<TextField
									fullWidth
									className="numberInput"
									disabled={clienteGeneraNumeracion}
									error={Boolean(error?.message)}
									helperText={error?.message}
									inputProps={{
										min: consecutivoDesde,
										max: consecutivoHasta,
										className: 'numberInput',
									}}
									onChange={onChange}
									onKeyPress={handleProcessNumberKeyPress}
									onPaste={handleProcessNumberPaste}
									placeholder="9.999.999"
									size="small"
									sx={{ marginTop: '8px' }}
									type="number"
									value={value}
								/>
							)}
						/>
						<Button
							onClick={() => {
								sincronizarRangoTest(data.id_rango);
							}}
							style={{
								width: '147px',
								height: '32px',
								borderRadius: '8px',
								boxShadow: '0px 3px 3px #00000029',
								textAlign: 'center',
								fontSize: '12px',
								fontWeight: 600,
								fontFamily: 'Montserrat',
								textTransform: 'none',
								letterSpacing: 0,
							}}
							sx={{ marginTop: '8px', marginBottom: '8px' }}
							variant="contained"
						>
							Sincronizar a Test
						</Button>
						<Controller
							name={`ranges.${index}.en_uso`}
							control={control}
							render={({ field: { onChange, value } }) => {
								return (
									<FormControlLabel
										value={value}
										onChange={onChange}
										control={<Switch checked={value} />}
										style={{ margin: 0 }}
										className="estadoRango"
										label={
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
											>
												Estado rango de guías
											</Typography>
										}
										labelPlacement="start"
									/>
								);
							}}
						/>
						<div>
							<hr
								style={{
									borderTop: '1px solid rgb(112 103 112 / 28%)',
									marginLeft: '-13px',
									marginRight: '-13px',
								}}
							></hr>
							<InformationContainer
								id={`process-edition-form-client-${data.id_rango}`}
								style={{
									paddingTop: 6,
									position: 'relative',
								}}
							>
								<IconButton
									onClick={() => setIsClientOpen(!isClientOpen)}
									id="buttonClient"
									size="small"
									sx={{ zIndex: 1, position: 'absolute', right: 12, top: 2 }}
								>
									{isClientOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
								</IconButton>
								{isClientOpen ? (
									<>
										<Typography
											color="#707070"
											fontFamily="Montserrat"
											fontSize={14}
											fontWeight={600}
										>
											Datos del cliente
										</Typography>
										<hr
											style={{
												borderTop: '1px solid rgb(112 103 112 / 28%)',
												marginLeft: '-13px',
												marginRight: '-13px',
												marginTop: '16px',
											}}
										></hr>
										<div style={{ marginTop: 16 }}>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
											>
												Cambiar Nombre o Razón Social
											</Typography>
											<Controller
												control={control}
												defaultValue={data.nombre}
												name={`ranges.${index}.nombre`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														className="numberInput"
														onChange={onChange}
														placeholder="Nombre o Razón Social"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Ciudad
											</Typography>
											<Controller
												control={control}
												name={`ranges.${index}.ciudad`}
												render={({ field: { onChange } }) => (
													<Autocomplete
														options={cities}
														getOptionLabel={(option) => option.nombre}
														style={{ marginTop: 8 }}
														value={calculateValue(data.ciudad)}
														onChange={(_e, v) => {
															setError(`ranges.${index}.ciudad`, {
																message: undefined,
															});
															if (typeof v === 'object') {
																setValue(
																	`ranges.${index}.ciudad`,
																	v?.codigo,
																	{ shouldDirty: true }
																);
																setValue(
																	`ranges.${index}.nombre_ciudad`,
																	v?.nombre,
																	{ shouldDirty: true }
																);
															}
														}}
														freeSolo
														renderInput={(params) => (
															<TextField
																{...params}
																size="small"
																onChange={onChange}
															/>
														)}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Dirección
											</Typography>
											<Controller
												control={control}
												defaultValue={data.direccion}
												name={`ranges.${index}.direccion`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														className="numberInput"
														onChange={onChange}
														placeholder="Dirección"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Teléfono
											</Typography>
											<Controller
												control={control}
												defaultValue={data.telefono}
												name={`ranges.${index}.telefono`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														className="numberInput"
														onChange={onChange}
														placeholder="Teléfono"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
											<Typography
												color="#707070"
												fontFamily="Montserrat"
												fontSize={14}
												fontWeight={500}
												style={{ marginTop: 16 }}
											>
												Cambiar Correo Electrónico
											</Typography>
											<Controller
												control={control}
												defaultValue={data.correo}
												name={`ranges.${index}.correo`}
												render={({ field: { onChange, value } }) => (
													<TextField
														fullWidth
														className="numberInput"
														onChange={onChange}
														placeholder="Correo Electrónico"
														size="small"
														sx={{ marginTop: '8px' }}
														type="text"
														value={value}
													/>
												)}
											/>
										</div>
									</>
								) : (
									<Typography
										color="#707070"
										fontFamily="Montserrat"
										fontSize={14}
										fontWeight={600}
										textAlign="left"
										letterSpacing={0}
									>
										Datos del cliente
									</Typography>
								)}
							</InformationContainer>
						</div>
					</>
				) : (
					<Typography
						color="#707070"
						component="div"
						fontFamily="Montserrat"
						fontSize={14}
						style={{ lineHeight: 1.6, opacity: !data.en_uso && !isOpen ? '0.3' : '1' }}
					>
						Prefijo: <b>{data.prefijo_consecutivo}</b>
						<br />
						Inicial:
						<Chip label={data.consecutivo_desde} size="small" variant="outlined" />
						Final:{' '}
						<Chip label={data.consecutivo_hasta} size="small" variant="outlined" />
						Actual:{' '}
						<Chip label={data.consecutivo_actual} size="small" variant="outlined" />
					</Typography>
				)}
			</InformationContainer>
		</>
	);
};

export default ProcessRangeHistoryCard;
